/**
 * Global keyboard shortcuts.
 *
 * Three classes of shortcut:
 *
 *   1. Modifier combos (Cmd/Ctrl + K, Cmd/Ctrl + /)
 *      - Fire immediately on keydown
 *      - preventDefault to override browser defaults where relevant
 *
 *   2. "Leader" navigation (g then d, g then e, g then h, g then t, g then c)
 *      - Press and release `g`, then within 1.2s press the next key
 *      - Skipped entirely while a text input, textarea, or contentEditable
 *        element is focused, so the user can still type the letter "g"
 *        into a field
 *
 *   3. Escape to close modals
 *      - A simple custom event (`nexus:escape`) anything can subscribe to,
 *        so modal components don't all need their own keydown listener
 *
 * The shortcut reference is duplicated in Help > Shortcuts. Edit both if
 * you add new bindings.
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const LEADER_TIMEOUT_MS = 1200;

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  // Some libraries mark custom editors with role=textbox
  if (el.getAttribute('role') === 'textbox') return true;
  return false;
}

export function useKeyboardShortcuts(): void {
  const navigate = useNavigate();
  const leaderActiveUntil = useRef<number>(0);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Modifier combos — work regardless of focus, because they're unambiguous
      const mod = e.metaKey || e.ctrlKey;

      // Cmd/Ctrl + K — open Ask Nexus
      if (mod && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        navigate('/query');
        return;
      }

      // Cmd/Ctrl + P — open command palette (Sidebar P2c, Wave 2 2026-05-21).
      // Dispatches a custom event so App.tsx (or wherever the palette is
      // mounted) can react. preventDefault is critical: browser's default
      // is "print page" — without preventDefault the user sees the print
      // dialog every time they hit Cmd+P.
      if (mod && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('nexus:command-palette-open'));
        return;
      }

      // Cmd/Ctrl + / — open Help
      if (mod && e.key === '/') {
        e.preventDefault();
        navigate('/help');
        return;
      }

      // Escape — broadcast a custom event that any open modal / sheet can listen for
      if (e.key === 'Escape') {
        window.dispatchEvent(new CustomEvent('nexus:escape'));
        // Don't preventDefault — native behavior (e.g., closing native date picker) stays intact
        return;
      }

      // Leader nav (g then <letter>) — skip if user is typing
      if (isTypingTarget(e.target)) return;

      const now = Date.now();
      if (leaderActiveUntil.current > now) {
        // We're in the "press second key" window
        const route = {
          d: '/', // Sprint 1: Command Center is now at /. Kept g+d for muscle memory.
          e: '/expenses',
          h: '/hours',
          t: '/tax',
          c: '/credentials',
          r: '/reports',
          p: '/payroll',
          i: '/invoices',
          m: '/mileage',
          q: '/query',
          s: '/settings',
          '?': '/help',
        }[e.key.toLowerCase()];
        leaderActiveUntil.current = 0;
        if (route) {
          e.preventDefault();
          navigate(route);
        }
        return;
      }

      // Start the leader window on plain `g`
      if (e.key === 'g' && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
        leaderActiveUntil.current = now + LEADER_TIMEOUT_MS;
        // No preventDefault — browser gets to handle if the user really meant "g"
        // somewhere outside our scope. If the second keypress doesn't come, we're idle.
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);
}
